/**
 * 星海场景：时间螺线上的照片星、装饰粒子（平时是背景天幕）、螺线引导线、年份、中心的心。
 * 入场动画由 intro.ts 驱动：它通过 beginIntro / onFrame / endIntro 接管镜头和两层星点。
 * 全屏流体（fluid.ts）：指针划过、点星或空白处、入场爆炸都会推动它，最后一道画面处理按它扭曲画面。
 * 辉光在 post.ts，看照片时跟着变弱。星轨（trail.ts）沿螺线从第一张照片走到最新一张；流星（meteors.ts）偶尔划过。
 * 轮转（carousel.ts）：这一天的照片排成光环时，镜头和手势交给光环，星海变暗。
 * 缩略图面片池（photos.ts）：靠近时星显影成照片。照片、轮转卡片、选中星的圈都画在叠加层上，色调映射之后才叠上来。
 * 语义缩放（semantic.ts）：年份、纪念日射线、月份刻度、回忆标题按镜头远近显出来；Esc 一层一层往外退。
 * 按帧时间自动降级（quality.ts）：掉帧时依次降像素比、流体、星点和照片、辉光，顺畅了再升回来。
 * 改了在一起的日子（setSince）：每颗星沿着自己那天的射线滑到新螺线上。
 * 照片放进回收站（remove）：星原地荡开一圈涟漪后熄灭，序号和位置留着，恢复时在原处重新亮起来。
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
  SPIRAL,
  StarPlacer,
  addDays,
  dayAnchor,
  dayRadius,
  daysBetween,
  hash01,
  localDateOf,
  toDayNumber,
  type LocalDate,
  type MemberDto,
  type MemberTint,
  type MemoryDto,
  type PhotoDto,
  type SpaceDto,
} from '@star-sea/shared';
import { Carousel, type CarouselHooks } from './carousel.ts';
import { Fluid } from './fluid.ts';
import { StarGrid } from './grid.ts';
import { heartSample, type HeartSample } from './heart.ts';
import { Meteors } from './meteors.ts';
import { PhotoPool } from './photos.ts';
import { pickStar } from './pick.ts';
import { ToneMapPass } from './post.ts';
import { QUALITY_LEVELS, QualityGovernor } from './quality.ts';
import { DAY_LEVEL, SemanticLayer, TITLE_MIN, YEAR_VIEW, type DayInfo } from './semantic.ts';
import { StarField } from './stars.ts';
import { StarTrail } from './trail.ts';

export interface GalaxyData {
  space: SpaceDto;
  members: MemberDto[];
  memories: MemoryDto[];
  photos: PhotoDto[];
}

const TINT: Record<MemberTint, THREE.Color> = { gold: new THREE.Color('#ffd79a'), rose: new THREE.Color('#ff9fbf') };
const FOV = 50;
const ELEVATION = THREE.MathUtils.degToRad(50);
/** 装饰粒子：入场时和照片星一起拼成爱心，爆炸后散到远处，成为背景天幕 */
const DUST_DESKTOP = 9000;
const DUST_MOBILE = 5000;
/** 指针划过时推动流体的力度，同原型 */
const FORCE = 5200;
/** 改了在一起的日子后星滑到新位置：离中心近的先动，最外圈晚这么多秒，像一圈波纹往外推 */
const MOVE_SPREAD = 0.7;
/** 星落定以后，螺线、年份、星轨、照片花这么久淡入（秒） */
const MOVE_FADE = 0.6;
/** 放进回收站的星：出生时间设成很远的将来，着色器不画、点选不中（stars.ts、pick.ts） */
const GONE = 1e9;

/** 星的大小：回忆越心动越大，每颗再随机大小一点 */
function starSize(shine: number, seed: number): number {
  return (0.55 + 0.11 * shine) * (0.8 + 0.4 * seed);
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** 照片主色往往偏灰，做成星要更亮更饱和；再带一点添加人的颜色 */
function starColor(hex: string, tint: MemberTint | undefined, out: THREE.Color): THREE.Color {
  const hsl = { h: 0, s: 0, l: 0 };
  out.set(hex).getHSL(hsl, THREE.SRGBColorSpace);
  out.setHSL(hsl.h, Math.min(0.95, 0.3 + hsl.s * 0.9), THREE.MathUtils.clamp(0.6 + (hsl.l - 0.5) * 0.35, 0.5, 0.78), THREE.SRGBColorSpace);
  if (tint) out.lerp(TINT[tint], 0.12);
  return out;
}

/**
 * 画好的 canvas 按像素数据（ImageData）上传，不直接把 canvas 元素交给 WebGL：
 * Safari 18 上传 canvas 元素、又要求不预乘透明度时，接近不透明的地方会混进零星的坏点，
 * 中心光晕里那团青色小点就是这么来的；Chrome 没有这个问题。
 */
function canvasTexture(c: HTMLCanvasElement): THREE.Texture {
  const t = new THREE.Texture(c.getContext('2d')!.getImageData(0, 0, c.width, c.height));
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

function glowTexture(inner: string, outer: string): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, inner);
  grad.addColorStop(0.25, outer);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  return canvasTexture(c);
}

function ringTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  g.strokeStyle = 'rgba(255, 236, 210, 0.95)';
  g.lineWidth = 5;
  g.beginPath();
  g.arc(64, 64, 52, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = 'rgba(255, 236, 210, 0.25)';
  g.lineWidth = 12;
  g.stroke();
  return canvasTexture(c);
}

interface Flight {
  fromPos: THREE.Vector3;
  fromTarget: THREE.Vector3;
  toPos: THREE.Vector3;
  toTarget: THREE.Vector3;
  t0: number;
  dur: number;
}

export interface CameraPose {
  pos: THREE.Vector3;
  target: THREE.Vector3;
}

export interface GalaxyStats {
  fps: number;
  frameMs: number;
  calls: number;
  stars: number;
}

export class Galaxy {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 6000);
  readonly controls: OrbitControls;
  readonly stars = new StarField(2048);
  readonly dust: StarField;
  /** 全屏流体；入场爆炸的冲击波也推它 */
  readonly fluid: Fluid;
  private readonly trail = new StarTrail();
  private readonly meteors = new Meteors();
  /** 叠加层：照片面片、轮转卡片、选中星的圈；不参与辉光和色调映射 */
  private readonly overlay = new THREE.Scene();
  private readonly grid = new StarGrid();
  /** 最大的星有多大：点选时大星的点中半径按它算 */
  private maxSize = 0;
  /** 缩略图面片池 */
  readonly pool: PhotoPool;
  /** 轮转光环；progress 上次用到的值（变了才改螺线、年份的可见度） */
  private readonly carousel: Carousel;
  private ringShown = -1;
  private readonly ndcV = new THREE.Vector2();
  /** 星轨开着没有（顶栏的开关）；trailK 是它当前的可见度，平滑地跟过去 */
  private trailOn = true;
  private trailK = 0;
  /** 星轨现在覆盖的照片日期；新照片超出这个范围才重建 */
  private trailRange: [string, string] | null = null;
  private readonly post = new ToneMapPass();
  readonly stats: GalaxyStats = { fps: 0, frameMs: 16.7, calls: 0, stars: 0 };
  photos: PhotoDto[] = [];
  onSelect: ((index: number) => void) | null = null;
  /** 每帧渲染前调用（入场动画用），参数是这一帧的时长和累计时间 */
  onFrame: ((dt: number, time: number) => void) | null = null;
  /** 入场期间镜头由入场动画控制，也不能点选 */
  private introActive = false;

  private space: SpaceDto | null = null;
  private placer: StarPlacer | null = null;
  private readonly index = new Map<string, number>();
  private readonly byDate = new Map<string, number[]>();
  private tints = new Map<string, MemberTint>();
  private shine = new Map<string, number>();
  private guide: THREE.Line | null = null;
  private guideDirty = false;
  /** 螺线、年份、中心光晕的可见度；入场期间为 0 */
  private scenery = 1;
  /** 0 是看星海，1 是在看照片：辉光跟着变弱、阈值变高，免得抢照片的戏（同原型的聚焦模式） */
  private bloomK = 0;
  /**
   * 辉光的强度、阈值、半径：[看星海, 看照片, 入场]。
   * 原型三者都是 0.9 / 0.2 / 0.55 起步，但这里的照片星比原型的星点亮得多（一天几百张会叠成一团），
   * 照搬的话星团会晕成一片；所以星海里只让真正亮的地方发光，入场的爱心用原型那样低的阈值。
   * 入场到螺线淡入时，从入场这组过渡回星海这组。
   */
  private readonly bloomLook = { strength: [0.7, 0.4, 0.9], threshold: [1.2, 1.8, 0.35], radius: [0.5, 0.4, 0.55] };
  private readonly core: THREE.Sprite;
  private readonly ring: THREE.Sprite;
  /** 年份、月份、纪念日、回忆标题（semantic.ts）；daysDirty 时重算哪些日子显标题 */
  private readonly semantic: SemanticLayer;
  private titles = new Map<string, string>();
  private daysDirty = false;
  private daysAt = 0;
  /** 星在滑向新螺线（setSince），到这个时刻全部落定；moveShown 是上次用到的淡入程度 */
  private moveEnd = -100;
  private moveShown = 1;
  /** 放进了回收站的星（序号）：不画、点不中、不显影，也不算在这一天的照片里 */
  private readonly gone = new Set<number>();
  /** 画质档位（quality.ts）：像素比上限、辉光开不开 */
  private readonly governor: QualityGovernor;
  private readonly onQuality: ((level: number) => void) | undefined;
  private readonly low: boolean;
  private dprCap = 2;
  private bloomOn = true;
  private selected = -1;
  /** 画面整体上移的比例（手机上打开底部面板时，让选中的星留在面板上方） */
  private shift = 0;
  private shiftTarget = 0;
  private flight: Flight | null = null;
  private time = 0;
  private last = performance.now();
  private outer = SPIRAL.r0;
  private readonly container: HTMLElement;
  private readonly labelLayer: HTMLElement;
  private readonly reducedMotion: boolean;
  /** 流体推力的倍数；减少动态效果时降到三成（同原型） */
  private readonly distort: number;
  private readonly pointer = { x: 0, y: 0, has: false };
  private readonly dyeColor = new THREE.Color();
  private readonly tmp = new THREE.Vector3();
  private readonly tmpColor = new THREE.Color();
  private readonly heart: HeartSample = { x: 0, y: 0, z: 0, kind: 0, bx: 0, by: 0, bz: 0 };
  private down: { x: number; y: number; t: number } | null = null;

  /**
   * thumbs：缩略图从哪来（压测模式没有照片，用主色渐变代替；token 给 Worker 取图用）。
   * quality：从哪一档画质开始、是不是固定不动、换档时通知谁（main.ts 记住档位）。
   */
  constructor(
    container: HTMLElement,
    labelLayer: HTMLElement,
    opts: { reducedMotion: boolean; thumbs: { fake: boolean; token: string | null }; quality: { start: number; fixed: boolean; onChange?: (level: number) => void } },
  ) {
    this.container = container;
    this.labelLayer = labelLayer;
    this.reducedMotion = opts.reducedMotion;
    this.renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    this.renderer.setClearColor('#04050c');
    // 一帧要画两次（场景、色调映射），绘制次数自己清零
    this.renderer.info.autoReset = false;
    container.appendChild(this.renderer.domElement);
    this.distort = this.reducedMotion ? 0.3 : 1;
    // 手机和小屏用低一档的分辨率（同原型）
    const low = (this.low = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 560);
    this.fluid = new Fluid(this.renderer, { simRes: low ? 96 : 128, dyeRes: low ? 192 : 320 });
    this.semantic = new SemanticLayer(labelLayer);
    this.scene.add(this.semantic.group);
    this.governor = new QualityGovernor({ start: opts.quality.start, fixed: opts.quality.fixed, now: performance.now() });
    this.onQuality = opts.quality.onChange;
    // 光环最多 12 格，纹理再多留几张（ADR-0004 的近档：桌面 64 槽、手机 24 槽，够用）
    this.carousel = new Carousel(this.overlay, { maxSlots: 12, spare: low ? 2 : 4, reduced: this.reducedMotion });
    // ADR-0004：中档 128px 桌面 1024 层、手机 384 层；近档 512px 桌面 64 层、手机 24 层
    this.pool = new PhotoPool(this.renderer, this.grid, { low, ...opts.thumbs });
    this.overlay.add(this.pool.mesh);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.07;
    this.controls.rotateSpeed = 0.55;
    this.controls.zoomSpeed = 0.9;
    this.controls.zoomToCursor = true;
    this.controls.minDistance = 1.2;
    this.controls.autoRotate = !this.reducedMotion;
    this.controls.autoRotateSpeed = 0.18;
    this.controls.addEventListener('start', () => {
      this.controls.autoRotate = false;
      this.flight = null;
    });

    this.stars.uniforms.uTwinkle.value = this.reducedMotion ? 0 : 1;
    if (this.reducedMotion) this.stars.uniforms.uMove.value = 0.01;
    // 照片星按日期先后落定，留出更大的先后差
    this.stars.uniforms.uStagger.value = 0.4;
    this.stars.mesh.renderOrder = 1;
    this.dust = this.makeDust(matchMedia('(pointer: coarse)').matches ? DUST_MOBILE : DUST_DESKTOP);
    this.dust.uniforms.uTwinkle.value = this.reducedMotion ? 0 : 1;
    this.dust.uniforms.uRestGain.value = 3;
    this.scene.add(this.dust.mesh);
    this.scene.add(this.stars.mesh);
    // 减少动态效果时彗星不走，只留一条静止的星轨
    this.trail.uniforms.uComet.value = this.reducedMotion ? 0 : 1;
    this.scene.add(this.trail.points, this.meteors.group);

    this.core = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: glowTexture('rgba(255,235,220,1)', 'rgba(255,140,175,0.35)'), blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0.7 }),
    );
    this.core.scale.setScalar(SPIRAL.r0 * 1.1);
    this.scene.add(this.core);

    // 选中星的圈画在叠加层上，盖在这颗星的照片上面；只加颜色、不写透明度，叠上去还是发光的样子
    this.ring = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: ringTexture(),
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        blending: THREE.CustomBlending,
        blendSrc: THREE.SrcAlphaFactor,
        blendDst: THREE.OneFactor,
        blendSrcAlpha: THREE.ZeroFactor,
        blendDstAlpha: THREE.OneFactor,
      }),
    );
    this.ring.visible = false;
    this.ring.renderOrder = 10;
    this.overlay.add(this.ring);

    const el = this.renderer.domElement;
    el.addEventListener('pointerdown', (e) => {
      this.down = { x: e.clientX, y: e.clientY, t: performance.now() };
      // 光环里拖动是转光环（镜头不动）
      if (this.carousel.active) {
        this.carousel.grab(e.clientX);
        el.setPointerCapture?.(e.pointerId);
      }
    });
    el.addEventListener('pointerup', (e) => {
      const d = this.down;
      this.down = null;
      this.carousel.release();
      if (this.introActive || !d || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6 || performance.now() - d.t > 600) return;
      // 星还在滑向新螺线：点到的位置和画出来的对不上，这两秒先不理
      if (this.time < this.moveEnd) return;
      if (this.carousel.active) return this.tapCarousel(e);
      const i = this.pickAt(e.clientX, e.clientY);
      // 点到空白处：那里荡开一圈涟漪；点到星的涟漪在 select 里
      if (i < 0) {
        const r = el.getBoundingClientRect();
        this.fluid.burst((e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height, 0.8 * this.distort);
      }
      this.select(i);
    });
    el.addEventListener('pointermove', (e) => {
      this.stir(e);
      if (!this.carousel.active) return;
      this.carousel.drag(e.clientX);
      if (!this.down) el.style.cursor = this.carousel.hover(this.ndc(e), this.camera) ? 'pointer' : '';
    });
    el.addEventListener('pointerleave', () => {
      this.pointer.has = false;
      this.carousel.hover(null, this.camera);
    });
    el.addEventListener('pointercancel', () => {
      this.down = null;
      this.carousel.release();
    });
    el.addEventListener(
      'wheel',
      (e) => {
        if (!this.carousel.active) return;
        e.preventDefault();
        this.carousel.wheel(Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY);
      },
      { passive: false },
    );

    new ResizeObserver(() => this.resize()).observe(container);
    this.applyQuality(this.governor.level);
    this.renderer.setAnimationLoop(() => this.frame());
  }

  /** 画质档位，0 最好（quality.ts） */
  get qualityLevel(): number {
    return this.governor.level;
  }

  /** 换到某一档画质：像素比、流体、辉光、装饰粒子、显影照片的上限 */
  private applyQuality(level: number): void {
    const q = QUALITY_LEVELS[level]!, u = this.post.uniforms;
    this.dprCap = q.dpr;
    this.bloomOn = q.bloom;
    this.fluid.enabled = q.fluid !== 'off';
    if (q.fluid === 'full') {
      this.fluid.setResolution(this.low ? 96 : 128, this.low ? 192 : 320);
      this.fluid.idleAfter = 10;
    } else if (q.fluid === 'lite') {
      // 低分辨率，而且只在推完之后 2 秒内算（design §10：只在交互时跑）
      this.fluid.setResolution(64, 128);
      this.fluid.idleAfter = 2;
    }
    // 流体关掉时最后一道画面处理也不扭曲、不染色
    u.uDistort.value = this.fluid.enabled ? 0.0002 : 0;
    u.uDye.value = this.fluid.enabled ? 0.32 : 0;
    this.dust.setLimit(Math.round(this.dust.count * q.dust));
    this.pool.setCap(q.photos);
    this.resize();
  }

  /** 装饰粒子：平时待在远处的球壳上当背景天幕，入场时拼成爱心。位置和颜色只由序号决定 */
  private makeDust(n: number): StarField {
    const dust = new StarField(n), c = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const id = `sky${i}`;
      const u = hash01(id, 1) * 2 - 1, a = hash01(id, 2) * Math.PI * 2, r = 1800 + hash01(id, 3) * 800, s = Math.sqrt(1 - u * u);
      const k = hash01(id, 4);
      c.setHSL(k < 0.12 ? 0.94 : k < 0.55 ? 0.6 : 0.09, k < 0.12 ? 0.6 : 0.4, 0.8, THREE.SRGBColorSpace);
      const h = heartSample(i + 1, this.heart);
      dust.push({
        x: Math.cos(a) * s * r,
        y: u * r,
        z: Math.sin(a) * s * r,
        color: c,
        size: (Math.pow(hash01(id, 5), 3.2) * 2.6 + 0.8) * 1.2,
        born: -100,
        seed: hash01(id, 6),
        heart: { x: h.x, y: h.y, z: h.z, kind: h.kind },
        burst: { x: h.bx, y: h.by, z: h.bz },
        order: hash01(id, 7),
      });
    }
    return dust;
  }

  /** 指针划过：顺着移动方向推一把流体，带一点玫瑰色（同原型） */
  private stir(e: PointerEvent): void {
    const r = this.renderer.domElement.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = 1 - (e.clientY - r.top) / r.height, p = this.pointer;
    if (p.has) {
      let dx = x - p.x, dy = y - p.y;
      const a = r.width / Math.max(1, r.height);
      if (a < 1) dx *= a;
      if (a > 1) dy /= a;
      if (dx || dy) {
        const sp = Math.min(1, Math.hypot(dx, dy) * 30), k = 0.06 * sp, c = this.dyeColor;
        c.setHSL((0.92 + 0.09 * Math.sin(this.time * 0.37)) % 1, 0.8, 0.55, THREE.SRGBColorSpace);
        this.fluid.splat(x, y, dx * FORCE * this.distort, dy * FORCE * this.distort, { r: c.r * k, g: c.g * k, b: c.b * k });
      }
    }
    p.x = x;
    p.y = y;
    p.has = true;
  }

  private ndc(e: PointerEvent): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return this.ndcV.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  }

  /** 光环里点了一下：正面那张回到照片面板，侧面的转到正面，空白处荡开涟漪 */
  private tapCarousel(e: PointerEvent): void {
    const hit = this.carousel.pick(this.ndc(e), this.camera);
    if (hit?.front) this.carousel.callbacks?.onOpen();
    else if (hit) this.carousel.spinTo(hit.slot);
    else {
      const r = this.renderer.domElement.getBoundingClientRect();
      this.fluid.burst((e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height, 0.8 * this.distort);
    }
  }

  /** 在某颗星的屏幕位置荡开一圈带它颜色的涟漪 */
  private burstAtStar(i: number, strength: number): void {
    const p = this.starPosition(i, this.tmp);
    if (!p) return;
    this.camera.updateMatrixWorld();
    p.project(this.camera);
    if (Math.abs(p.x) > 1.2 || Math.abs(p.y) > 1.2 || p.z > 1) return;
    const c = this.stars.colors, k = 0.05;
    this.fluid.burst((p.x + 1) / 2, (p.y + 1) / 2, strength * this.distort, { r: c[i * 3]! * k, g: c[i * 3 + 1]! * k, b: c[i * 3 + 2]! * k });
  }

  private resize(): void {
    const w = Math.max(1, this.container.clientWidth), h = Math.max(1, this.container.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h);
    this.post.setSize(Math.round(w * dpr), Math.round(h * dpr));
    this.fluid.resize(w, h);
    this.camera.aspect = w / h;
    if (this.shift > 0) this.camera.setViewOffset(w, h, 0, this.shift * h, w, h);
    else this.camera.updateProjectionMatrix();
    for (const layer of [this.stars, this.dust]) {
      layer.uniforms.uViewH.value = h * dpr;
      layer.uniforms.uMaxPx.value = 90 * dpr;
    }
    this.stars.uniforms.uMinPx.value = 2.4 * dpr;
    this.dust.uniforms.uMinPx.value = 1.6 * dpr;
    this.trail.uniforms.uViewH.value = h * dpr;
    this.trail.uniforms.uDpr.value = dpr;
    // 横竖屏换了：镜头重新对准光环正面
    if (this.carousel?.active) {
      const pose = this.carousel.cameraPose(this.camera.aspect, this.camera.fov);
      this.flyTo(pose.pos, pose.target, 0.5);
    }
  }

  /** 整体载入。reveal 时按日期从里往外依次点亮 */
  load(data: GalaxyData, reveal: boolean): void {
    this.space = data.space;
    this.placer = new StarPlacer(data.space.since);
    this.tints = new Map(data.members.map((m) => [m.id, m.tint]));
    this.shine = new Map(data.memories.map((m) => [m.id, m.shine]));
    this.titles = new Map(data.memories.flatMap((m) => (m.title ? [[m.id, m.title] as const] : [])));
    this.photos = [];
    this.index.clear();
    this.byDate.clear();
    this.gone.clear();
    this.grid.clear();
    this.maxSize = 0;
    this.pool.reset();
    this.outer = SPIRAL.r0;
    this.stars.reset(data.photos.length);
    this.select(-1, false);
    let lo = Number.POSITIVE_INFINITY, hi = Number.NEGATIVE_INFINITY;
    for (const p of data.photos) {
      const d = toDayNumber(p.date);
      lo = Math.min(lo, d);
      hi = Math.max(hi, d);
    }
    const t0 = this.time + 0.3, span = Math.max(1, hi - lo);
    for (const p of data.photos) {
      const order = (toDayNumber(p.date) - lo) / span;
      this.add(p, reveal ? t0 + 2.6 * order + hash01(p.id, 9) * 0.4 : -100, Math.min(1, order * 0.85 + hash01(p.id, 10) * 0.15));
    }
    this.rebuildGuide();
    this.frameAll(false);
  }

  /** 加一颗星（已存在则返回原来的序号）；born 默认现在，会闪一下。order 是入场爆炸后落定的先后 */
  add(photo: PhotoDto, born = this.time, order = 1): number {
    const known = this.index.get(photo.id);
    if (known !== undefined) {
      // 从回收站恢复：在原来的位置重新亮起来（闪一下）
      if (this.gone.has(known)) this.revive(known);
      return known;
    }
    if (!this.placer) throw new Error('星海还没载入');
    const v = this.placer.place(photo, this.tmp);
    const i = this.photos.length;
    this.photos.push(photo);
    this.index.set(photo.id, i);
    this.grid.add(i, v.x, v.y, v.z);
    let day = this.byDate.get(photo.date);
    if (!day) this.byDate.set(photo.date, (day = []));
    day.push(i);
    // 这一天的照片多到可以显标题了，或者张数变了
    if (day.length >= TITLE_MIN) this.daysDirty = true;
    // 比星轨现有的范围更早或更晚的照片：星轨要接长
    const range = this.trailRange;
    if (!range || photo.date < range[0] || photo.date > range[1]) this.guideDirty = true;
    const seed = hash01(photo.id, 5);
    const size = starSize(this.shine.get(photo.memoryId) ?? 3, seed);
    this.maxSize = Math.max(this.maxSize, size);
    // 照片在爱心上的位置由照片 id 决定，每次打开都一样
    const h = heartSample(Math.floor(hash01(photo.id, 11) * 4294967296), this.heart);
    this.stars.push({
      x: v.x,
      y: v.y,
      z: v.z,
      color: starColor(photo.color, this.tints.get(photo.createdBy), this.tmpColor),
      size,
      born,
      seed,
      heart: { x: h.x, y: h.y, z: h.z, kind: h.kind },
      burst: { x: h.bx, y: h.by, z: h.bz },
      order,
    });
    const r = Math.hypot(v.x, v.z);
    if (r > this.outer + 0.5) {
      this.outer = r;
      this.guideDirty = true;
    }
    return i;
  }

  /** 照片放进了回收站：星原地荡开一圈涟漪后熄灭。返回它的序号；不认识的照片返回 -1 */
  remove(id: string): number {
    const i = this.index.get(id);
    if (i === undefined) return -1;
    if (this.gone.has(i)) return i;
    this.burstAtStar(i, 0.8);
    this.gone.add(i);
    const p = this.stars.positions;
    this.grid.remove(i, p[i * 3]!, p[i * 3 + 1]!, p[i * 3 + 2]!);
    this.stars.setBorn(i, GONE);
    if (this.selected === i) this.ring.visible = false;
    this.daysDirty = true;
    return i;
  }

  private revive(i: number): void {
    this.gone.delete(i);
    const p = this.stars.positions;
    this.grid.add(i, p[i * 3]!, p[i * 3 + 1]!, p[i * 3 + 2]!);
    this.stars.setBorn(i, this.time);
    this.daysDirty = true;
  }

  isGone(i: number): boolean {
    return this.gone.has(i);
  }

  /** 亮着的星有多少颗（放进回收站的不算） */
  get count(): number {
    return this.photos.length - this.gone.size;
  }

  /** 亮着的照片（存离线快照用） */
  livePhotos(): PhotoDto[] {
    return this.gone.size ? this.photos.filter((_, i) => !this.gone.has(i)) : this.photos;
  }

  /** 这段回忆里第一颗亮着的星；没有返回 -1 */
  firstOf(memoryId: string): number {
    for (let i = 0; i < this.photos.length; i++) if (this.photos[i]!.memoryId === memoryId && !this.gone.has(i)) return i;
    return -1;
  }

  /** 回忆来了或者改了：心动变了，这段回忆的星跟着变大变小；标题变了，回忆标题重算 */
  setMemory(m: MemoryDto): void {
    const shine = this.shine.get(m.id);
    this.shine.set(m.id, m.shine);
    if (shine !== undefined && shine !== m.shine) {
      for (let i = 0; i < this.photos.length; i++) {
        const p = this.photos[i]!;
        if (p.memoryId !== m.id) continue;
        const size = starSize(m.shine, hash01(p.id, 5));
        this.stars.setSize(i, size);
        this.maxSize = Math.max(this.maxSize, size);
      }
    }
    if ((this.titles.get(m.id) ?? null) === (m.title ?? null)) return;
    if (m.title) this.titles.set(m.id, m.title);
    else this.titles.delete(m.id);
    this.daysDirty = true;
  }

  /**
   * 在一起的日子改了：每颗星按新的螺线重新放。角度只看日期，所以每颗星都是沿着自己那天的射线滑到新的半径上。
   * 螺线、年份、星轨直接换成新的，等星落定了再淡入；滑动的这两秒里照片先隐去，也不能点选（photos.ts、pointerup）。
   * 镜头：开着照片就跟着那颗星过去，否则退到能看到整片新星海的位置。
   */
  setSince(since: LocalDate): void {
    if (!this.space || this.space.since === since) return;
    this.space = { ...this.space, since };
    this.placer = new StarPlacer(since);
    this.grid.clear();
    this.outer = SPIRAL.r0;
    const n = this.photos.length, next = new Float32Array(n * 3), v = this.tmp;
    for (let i = 0; i < n; i++) {
      this.placer.place(this.photos[i]!, v);
      next.set([v.x, v.y, v.z], i * 3);
      if (!this.gone.has(i)) this.grid.add(i, v.x, v.y, v.z);
      this.outer = Math.max(this.outer, Math.hypot(v.x, v.z));
    }
    const t0 = this.time, spread = this.reducedMotion ? 0 : MOVE_SPREAD;
    for (let i = 0; i < n; i++) {
      const x = next[i * 3]!, y = next[i * 3 + 1]!, z = next[i * 3 + 2]!;
      this.stars.moveTo(i, x, y, z, t0 + spread * Math.min(1, Math.hypot(x, z) / this.outer), t0);
    }
    this.moveEnd = t0 + spread + this.stars.uniforms.uMove.value;
    this.rebuildGuide();
    const i = this.selected;
    if (i >= 0 && i < n) {
      const p = new THREE.Vector3().fromArray(next, i * 3), d = this.camera.position.distanceTo(this.controls.target);
      const dir = this.camera.position.clone().sub(this.controls.target).normalize();
      this.flyTo(p.clone().addScaledVector(dir, d), p, this.moveEnd - t0);
    } else this.frameAll(true);
  }

  /** 星落定以后螺线、年份、星轨、照片的淡入程度：没在滑时是 1，滑的时候是 0 */
  private moveVisibility(): number {
    return THREE.MathUtils.clamp((this.time - this.moveEnd) / MOVE_FADE, 0, 1);
  }

  /** 照片多的日子和它们的回忆标题（一天一段回忆，取那天第一张照片所属的回忆） */
  private dayInfo(): DayInfo[] {
    const out: DayInfo[] = [];
    for (const [date, all] of this.byDate) {
      const list = this.gone.size ? all.filter((i) => !this.gone.has(i)) : all;
      if (list.length < TITLE_MIN) continue;
      out.push({ date: date as LocalDate, count: list.length, title: this.titles.get(this.photos[list[0]!]!.memoryId) ?? null });
    }
    return out;
  }

  setMember(m: MemberDto): void {
    this.tints.set(m.id, m.tint);
  }

  indexOf(id: string): number {
    return this.index.get(id) ?? -1;
  }

  /** 同一天的星，按添加顺序 */
  sameDay(i: number): number[] {
    const p = this.photos[i];
    if (!p) return [];
    const day = this.byDate.get(p.date) ?? [i];
    return this.gone.size ? day.filter((k) => !this.gone.has(k)) : day;
  }

  private rebuildGuide(): void {
    this.guideDirty = false;
    if (!this.space) return;
    const since = this.space.since;
    let first: LocalDate = since, lastDate: LocalDate = localDateOf(new Date());
    for (const d of this.byDate.keys()) {
      if (d < first) first = d as LocalDate;
      if (d > lastDate) lastDate = d as LocalDate;
    }
    const end = addDays(lastDate, 30), days = daysBetween(first, end);
    // 照片还少的时候，也要把到今天为止的整条螺线放进画面
    this.outer = Math.max(this.outer, dayRadius(end, since));
    const pts = new Float32Array((Math.floor(days / 2) + 1) * 3);
    const a = { x: 0, y: 0, z: 0 };
    for (let k = 0, d = 0; d <= days; d += 2, k++) {
      dayAnchor(addDays(first, d), since, SPIRAL, a);
      pts.set([a.x, a.y, a.z], k * 3);
    }
    if (this.guide) {
      this.scene.remove(this.guide);
      this.guide.geometry.dispose();
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    this.guide = new THREE.Line(g, new THREE.LineBasicMaterial({ color: '#8090ff', transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.scene.add(this.guide);
    this.applyScenery();
    // 星轨从第一张照片走到最新一张，再虚线延到 end
    let photoFirst: string | null = null, photoLast: string | null = null;
    for (const d of this.byDate.keys()) {
      if (!photoFirst || d < photoFirst) photoFirst = d;
      if (!photoLast || d > photoLast) photoLast = d;
    }
    if (photoFirst && photoLast) {
      this.trail.rebuild(photoFirst as LocalDate, photoLast as LocalDate, end, since);
      this.trailRange = [photoFirst, photoLast];
    } else {
      this.trail.clear();
      this.trailRange = null;
    }
    this.semantic.rebuild({ since, first, end, hasPhotos: this.byDate.size > 0, days: this.dayInfo() });
    this.daysDirty = false;
  }

  /** 能看到整片星海的镜头位置 */
  overviewPose(): CameraPose {
    const R = this.outer + 6;
    const halfV = THREE.MathUtils.degToRad(FOV / 2);
    const fitV = R / Math.tan(halfV), fitH = R / (Math.tan(halfV) * this.camera.aspect);
    const dist = Math.max(fitV, fitH) * 1.05;
    return { pos: new THREE.Vector3(0, Math.sin(ELEVATION) * dist, Math.cos(ELEVATION) * dist), target: new THREE.Vector3() };
  }

  /**
   * Esc 退一层（design §3.2 的流程图）：一天 → 一年 → 整片星海。
   * 已经在看整片星海、正在入场或者在轮转里时返回 false。
   */
  stepBack(): boolean {
    if (this.introActive || this.carousel.active) return false;
    const target = this.controls.target, d = this.camera.position.distanceTo(target);
    if (d < DAY_LEVEL) {
      // 一天 → 一年：注视点不动，沿着现在的方向退到能看到一圈螺线的距离
      const dir = this.camera.position.clone().sub(target).normalize();
      this.flyTo(target.clone().addScaledVector(dir, YEAR_VIEW), target.clone(), 1.2);
      return true;
    }
    const home = this.overviewPose();
    if (d < home.pos.distanceTo(home.target) * 0.85 || target.distanceTo(home.target) > 1) {
      this.frameAll(true);
      return true;
    }
    return false;
  }

  /** 镜头退回能看到整片星海的位置 */
  frameAll(animated = true): void {
    const { pos, target } = this.overviewPose();
    this.controls.maxDistance = pos.length() * 2.5;
    if (animated) this.flyTo(pos, target, 1.4);
    else {
      this.camera.position.copy(pos);
      this.controls.target.copy(target);
      this.controls.update();
    }
  }

  /** 两层星点（装饰粒子、照片星） */
  get layers(): StarField[] {
    return [this.dust, this.stars];
  }

  /** 最后一道画面处理的参数（白光、撕裂、流体扭曲的强度） */
  get postUniforms(): ToneMapPass['uniforms'] {
    return this.post.uniforms;
  }

  /** 提前分配照片的纹理数组（入场爆炸的白光里调，见 PhotoPool.warm） */
  warmUp(): void {
    this.pool.warm();
  }

  /** 打开或关上星轨 */
  setTrail(on: boolean): void {
    this.trailOn = on;
  }

  /** 螺线、年份、中心光晕的可见度，0–1 */
  setScenery(k: number): void {
    this.scenery = k;
    this.applyScenery();
  }

  /** 轮转时螺线、年份淡出，中心光晕暗一半（同原型） */
  private applyScenery(): void {
    const ring = this.carousel.progress, k = this.scenery * (1 - ring) * this.moveShown;
    (this.core.material as THREE.SpriteMaterial).opacity = 0.7 * this.scenery * (1 - 0.5 * ring);
    if (this.guide) (this.guide.material as THREE.LineBasicMaterial).opacity = 0.1 * k;
    this.labelLayer.style.opacity = String(k);
  }

  /**
   * 轮转这一天的照片：stars 按添加顺序，start 是正面那张在里面的位置。
   * 光环悬在星团上方、正面朝着镜头现在的方向；镜头飞过去，手势交给光环。
   */
  enterCarousel(stars: number[], start: number, hooks: CarouselHooks): void {
    if (this.introActive || stars.length < 2) return;
    this.select(-1, false);
    const center = new THREE.Vector3(), p = new THREE.Vector3();
    for (const i of stars) if (this.starPosition(i, p)) center.add(p);
    center.divideScalar(stars.length);
    const aspects = stars.map((i) => {
      const ph = this.photos[i];
      return ph?.width && ph.height ? ph.width / ph.height : 4 / 3;
    });
    this.carousel.enter({ stars, start, aspects, center, facing: this.camera.position, hooks });
    this.controls.enabled = false;
    this.controls.autoRotate = false;
    const pose = this.carousel.cameraPose(this.camera.aspect, this.camera.fov);
    this.flyTo(pose.pos, pose.target, 1.9);
  }

  /** 收起光环，照片飞回各自的星；返回正面那张星的序号 */
  leaveCarousel(): number {
    const front = this.carousel.front;
    this.carousel.leave();
    this.controls.enabled = true;
    this.renderer.domElement.style.cursor = '';
    return front;
  }

  /** 光环展开着（收起的动画开始后就不算了） */
  get inCarousel(): boolean {
    return this.carousel.active;
  }

  /** 光环转一格 */
  carouselStep(d: number): void {
    if (this.carousel.active) this.carousel.step(d);
  }

  /** 入场开始：镜头和两层星点交给入场动画，不能点选 */
  beginIntro(): void {
    this.introActive = true;
    this.flight = null;
    this.controls.enabled = false;
    this.controls.autoRotate = false;
    this.select(-1, false);
    this.setScenery(0);
    for (const layer of this.layers) layer.uniforms.uBang.value = 0;
  }

  /** 入场结束：镜头停在 pose，交还给手势控制 */
  endIntro(pose: CameraPose): void {
    this.introActive = false;
    for (const layer of this.layers) {
      layer.uniforms.uBang.value = 1;
      layer.uniforms.uOpacity.value = 1;
    }
    this.camera.position.copy(pose.pos);
    this.controls.target.copy(pose.target);
    this.controls.maxDistance = pose.pos.distanceTo(pose.target) * 2.5;
    this.controls.enabled = true;
    this.controls.autoRotate = !this.reducedMotion;
    this.controls.update();
    this.setScenery(1);
  }

  private flyTo(pos: THREE.Vector3, target: THREE.Vector3, dur: number): void {
    this.flight = {
      fromPos: this.camera.position.clone(),
      fromTarget: this.controls.target.clone(),
      toPos: pos.clone(),
      toTarget: target.clone(),
      t0: this.time,
      dur: this.reducedMotion ? 0.01 : dur,
    };
  }

  /** 镜头飞到某颗星跟前，保持当前的观察方向 */
  focus(i: number, distance = 7): void {
    if (i < 0 || i >= this.stars.count) return;
    // 星还在滑的话，飞到它要落定的地方
    const p = new THREE.Vector3().fromArray(this.stars.positions, i * 3);
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.flyTo(p.clone().addScaledVector(dir, distance), p, 1.3);
  }

  /** 某颗星现在在屏幕上的位置（CSS 像素，相对于视口）；在镜头背后或者画面外很远时返回 null */
  screenPosition(i: number): { x: number; y: number } | null {
    const p = this.starPosition(i, this.tmp);
    if (!p) return null;
    this.camera.updateMatrixWorld();
    p.project(this.camera);
    if (p.z > 1 || Math.abs(p.x) > 1.5 || Math.abs(p.y) > 1.5) return null;
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((p.x + 1) / 2) * r.width, y: r.top + ((1 - p.y) / 2) * r.height };
  }

  /** 星现在画在哪（滑向新螺线的途中是半路上的位置） */
  starPosition(i: number, out = new THREE.Vector3()): THREE.Vector3 | null {
    if (i < 0 || i >= this.stars.count) return null;
    return this.stars.displayed(i, this.time, out);
  }

  select(i: number, fly = true): void {
    // 放进回收站的星选不了（比如点了一条旧提示里的「去看看」）
    const p = i >= 0 && !this.gone.has(i) ? this.starPosition(i) : null;
    this.selected = p ? i : -1;
    this.ring.visible = p !== null;
    if (p) {
      this.ring.position.copy(p);
      this.controls.autoRotate = false;
      if (fly) {
        this.burstAtStar(i, 1);
        this.focus(i);
      }
    }
    this.onSelect?.(p ? i : -1);
  }

  get selectedIndex(): number {
    return this.selected;
  }

  /** 0 到 0.5：画面中心上移屏幕高度的这个比例 */
  setViewShift(fraction: number): void {
    this.shiftTarget = THREE.MathUtils.clamp(fraction, 0, 0.5);
  }

  /** 屏幕坐标下最近的一颗星；没有返回 -1。用空间网格只查点击射线附近的星（pick.ts） */
  pickAt(clientX: number, clientY: number): number {
    const rect = this.renderer.domElement.getBoundingClientRect();
    return pickStar({
      camera: this.camera,
      x: clientX - rect.left,
      y: clientY - rect.top,
      width: rect.width,
      height: rect.height,
      positions: this.stars.positions,
      sizes: this.stars.sizes,
      borns: this.stars.borns,
      count: this.stars.count,
      time: this.time,
      grid: this.grid,
      maxSize: this.maxSize,
    });
  }

  private frame(): void {
    const now = performance.now();
    // 画质：按帧间隔自动换档（页面在后台时浏览器不调这里，回来的第一帧间隔很长，那种不算）
    const next = this.governor.sample(now - this.last, now);
    if (next !== null) {
      this.applyQuality(next);
      this.onQuality?.(next);
    }
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.stats.frameMs += (now - this.last - this.stats.frameMs) * 0.05;
    this.stats.fps = 1000 / Math.max(1, this.stats.frameMs);
    this.last = now;
    this.time += dt;

    const f = this.flight;
    if (f && !this.introActive) {
      const k = Math.min(1, (this.time - f.t0) / f.dur), e = easeInOut(k);
      this.camera.position.lerpVectors(f.fromPos, f.toPos, e);
      this.controls.target.lerpVectors(f.fromTarget, f.toTarget, e);
      if (k >= 1) this.flight = null;
    }
    if (!this.introActive) this.controls.update(dt);
    this.onFrame?.(dt, this.time);
    if (this.shift !== this.shiftTarget) {
      this.shift += (this.shiftTarget - this.shift) * (this.reducedMotion ? 1 : 1 - Math.exp(-dt * 6));
      if (Math.abs(this.shift - this.shiftTarget) < 1e-4) this.shift = this.shiftTarget;
      const w = this.container.clientWidth, h = this.container.clientHeight;
      if (this.shift > 0) this.camera.setViewOffset(w, h, 0, this.shift * h, w, h);
      else this.camera.clearViewOffset();
    }

    // 中心的心：很轻的“咚—咚”
    const ph = (this.time % 1.3) / 1.3;
    const beat = this.reducedMotion ? 0 : Math.exp(-(((ph - 0.05) / 0.05) ** 2)) + 0.55 * Math.exp(-(((ph - 0.28) / 0.06) ** 2));
    this.core.scale.setScalar(SPIRAL.r0 * (1.1 + beat * 0.06));
    (this.ring.material as THREE.SpriteMaterial).opacity = 0.75 + 0.25 * Math.sin(this.time * 3);
    this.ring.scale.setScalar(0.045 + 0.004 * Math.sin(this.time * 3));

    // 星在滑向新螺线：选中的圈跟着星走；落定以后螺线、年份这些淡入
    if (this.time < this.moveEnd && this.selected >= 0) this.starPosition(this.selected, this.ring.position);
    const settled = this.moveVisibility();
    if (settled !== this.moveShown) {
      this.moveShown = settled;
      this.applyScenery();
    }

    if (this.guideDirty) this.rebuildGuide();
    // 加照片很快时（一次传几百张），回忆标题最多一秒重算一次
    else if (this.daysDirty && this.time - this.daysAt > 1) {
      this.daysDirty = false;
      this.daysAt = this.time;
      this.semantic.setDays(this.dayInfo());
    }
    // 轮转：卡片在星和光环之间飞；星海暗到一半，螺线、年份、星轨淡出（同原型）
    this.carousel.update(dt, this.camera, (i, out) => this.starPosition(i, out), (i, out) => out.fromArray(this.stars.colors, i * 3));
    const ring = this.carousel.progress;
    if (!this.introActive) for (const layer of this.layers) layer.uniforms.uOpacity.value = 1 - 0.5 * ring;
    // 没有入场动画时，打开 1.5 秒后预热照片的纹理数组
    if (!this.introActive && this.time > 1.5) this.pool.warm();
    // 照片面片：入场时星还在爱心上，不显影；轮转时让位给光环
    this.pool.update({ time: this.time, camera: this.camera, viewH: this.container.clientHeight, stars: this.stars, photos: this.photos, opacity: this.introActive ? 0 : this.scenery * (1 - ring) * settled });
    if (ring !== this.ringShown) {
      this.ringShown = ring;
      this.applyScenery();
    }
    // 星轨：入场时随螺线一起从第一段回忆画到最后；看照片时淡一些（同原型的聚焦模式）
    this.trailK += ((this.trailOn ? (this.selected >= 0 ? 0.45 : 1) : 0) - this.trailK) * (1 - Math.exp(-3 * dt));
    const tu = this.trail.uniforms;
    tu.uOpacity.value = this.trailK * (1 - ring) * settled;
    tu.uDraw.value = this.scenery * (tu.uEnd.value + 0.01);
    tu.uTime.value = this.time;
    this.meteors.update(dt, this.time, this.camera, !this.reducedMotion && !this.introActive && this.selected < 0 && ring === 0);
    for (const layer of this.layers) {
      layer.uniforms.uTime.value = this.time;
      layer.commit();
    }
    this.fluid.step(dt);
    const u = this.post.uniforms;
    u.tVel.value = this.fluid.velocityTexture;
    u.tDye.value = this.fluid.dyeTexture;
    u.uTime.value = this.time;
    // 辉光：看照片时变弱、阈值变高，免得抢照片的戏；入场白光时一起冲高（同原型）
    this.bloomK += ((this.selected >= 0 ? 1 : 0) - this.bloomK) * (1 - Math.exp(-2.5 * dt));
    const bloom = this.post.bloom, k = this.bloomK, intro = 1 - this.scenery, L = this.bloomLook, lerp = THREE.MathUtils.lerp;
    const look = (v: number[]) => lerp(lerp(v[0]!, v[1]!, k), v[2]!, intro);
    // 画质降到关辉光那一档时，强度 0 就整个跳过（post.ts）
    bloom.strength = this.bloomOn ? look(L.strength) + u.uFlash.value * 1.4 : 0;
    bloom.threshold = look(L.threshold);
    bloom.radius = look(L.radius);
    // 「绘制次数」只数场景本身，看的是星点有没有合批；流体、辉光、最后合成的那些遍不算
    this.renderer.info.reset();
    const overlay = this.carousel.visible || this.pool.visible || this.ring.visible;
    this.post.render(this.renderer, this.scene, this.camera, overlay ? this.overlay : null);
    this.stats.calls = this.post.sceneCalls;
    this.stats.stars = this.stars.count;
    this.semantic.update({ camera: this.camera, target: this.controls.target, width: this.container.clientWidth, height: this.container.clientHeight, k: this.scenery * (1 - ring) * settled });
  }
}
